// Central content for the Illinois Broker Prep storefront.

export const CONTACT_EMAIL = "support@illinoisbrokerprep.com";

export const PRODUCT = {
  title: "Illinois Broker Prep Online Study Platform",
  price: "79",
  shortDesc:
    "An Illinois-first learning and practice system with source-backed lessons, topic drills, National and Illinois exam simulations, Question Coach help, a Word Bank, and readiness tracking. Current question-bank coverage is published transparently on the Coverage page.",
  accessNote:
    "Use the same email at checkout as your student account. Full access unlocks automatically after payment, and we email your start link.",
};

export const ACCESS_TIMING_TEXT =
  "Full access connects to the email used at checkout. Your start email is sent automatically after payment, with support backup for any delivery delay.";

export const HERO = {
  headline:
    "Illinois broker exam prep that’s actually built for Illinois.",
  subhead:
    "Learn Illinois law, build national fundamentals, and practice under timed conditions with source-backed lessons, topic drills, a Question Coach grounded in each question, and separate National and Illinois simulations. One purchase, lifetime access.",
  primaryCta: "Try 10 questions free",
  secondaryCta: "Get full access — $79",
};

export const HERO_CHIPS = [
  { icon: "MonitorPlay", label: "Interactive practice access" },
  { icon: "ShieldCheck", label: "Secure checkout" },
  { icon: "MapPin", label: "Illinois-focused" },
];

// Homepage \"Problem\" section
export const PROBLEMS = [
  {
    icon: "MapPin",
    title: "National courses teach national law",
    desc: "Illinois uses its own licensing rules, disclosures, escrow requirements, and agency framework. Generic national materials can leave gaps on the state portion.",
  },
  {
    icon: "BookOpen",
    title: "Illinois calls the entry-level license Broker",
    desc: "That alone trips people up when they study from older or generic materials. The product is written around the Illinois broker exam candidates actually take.",
  },
  {
    icon: "RefreshCw",
    title: "Practice needs feedback",
    desc: "Answering questions is not enough. You need explanations, source references, missed-question review, and a way to see what still needs work.",
  },
];

// Homepage bento (6 cards)
export const HOME_FEATURES = [
  {
    icon: "FileText",
    title: "Illinois-first lessons",
    desc: "Learn the rule, the vocabulary, and the exam traps before you start drilling questions.",
  },
  {
    icon: "MapPin",
    title: "Source-backed Illinois practice",
    desc: "State-law questions connect the answer to the statute, rule, or source behind it.",
  },
  {
    icon: "Globe",
    title: "National concepts refresh",
    desc: "Build the nationwide ownership, contracts, finance, agency, and transfer-of-title foundations.",
  },
  {
    icon: "Sigma",
    title: "Real estate math drills",
    desc: "Work through commissions, prorations, ratios, and area problems one clear step at a time.",
  },
  {
    icon: "Timer",
    title: "Exact-length exam simulations",
    desc: "A 100-question National simulation, a 40-question Illinois simulation, and shorter Timed Review Sprints.",
  },
  {
    icon: "Sparkles",
    title: "Question Coach",
    desc: "After you answer, ask why it is right, why the other choices are wrong, or what the source is testing.",
  },
];

export const ILLINOIS_POINTS = [
  {
    icon: "Scale",
    title: "Illinois rules are different",
    desc: "Agency, escrow, disclosure, and licensing rules are framed around Illinois law instead of generic national-only prep.",
  },
  {
    icon: "BookOpen",
    title: "Statute-cited state practice",
    desc: "Illinois-specific questions include source references so you can see what rule or statute the question is testing.",
  },
  {
    icon: "Target",
    title: "Illinois-first teaching",
    desc: "Short lessons teach the Illinois framework before you test yourself under real exam-style pressure.",
  },
];

export const HOW_IT_WORKS = [
  {
    icon: "Target",
    title: "Learn the rule",
    desc: "Start with the free 10-question diagnostic, then use the Illinois-first learning path to understand what each topic is actually testing.",
  },
  {
    icon: "BookOpen",
    title: "Recall and drill",
    desc: "Review key terms, practice by topic, and work through missed questions with source-cited explanations and the Question Coach.",
  },
  {
    icon: "TrendingUp",
    title: "Simulate the real pressure",
    desc: "Run separate National and Illinois simulations using the official question counts and time limits.",
  },
];

// Product page \"What’s included\" (full list) — interactive online study access
export const INCLUDED_FULL = [
  "Lifetime access to the interactive Illinois Broker Prep online study platform — yours until you pass",
  "Free 10-question diagnostic",
  "Source-backed lessons across Illinois law, national concepts, and math foundations",
  "Current active question library: 356 original questions across Illinois state law, national concepts, and real estate math",
  "217 Illinois-specific questions tied to statutes, rules, and source references",
  "98 national real estate questions and 41 math drills",
  "100-question, 150-minute National Broker simulation",
  "40-question, 90-minute Illinois Broker simulation",
  "Optional 140-question combined stamina session",
  "Built-in Question Coach on answered questions",
  "Readiness indicator",
  "Weak-area tracking",
  "Missed-question review",
  "Bookmarks",
  "Word Bank concept index and lesson key terms",
  "Timed Review Sprints",
  "Plain-English explanations with source references",
  "Free updates as the question bank expands",
];

export const WHO_FOR = [
  "Candidates who completed or are finishing the Illinois 75-hour broker course",
  "Candidates testing in the next 1–6 weeks",
  "Candidates who failed once and do not want to retake blind",
  "Candidates who want Illinois-specific practice, not just generic national prep",
];

export const WHO_NOT_FOR = [
  "People looking for the required 75-hour pre-license course",
  "People expecting official PSI questions",
  "People looking for a guaranteed pass promise",
  "People looking for a replacement for their required pre-license education",
];

export const TRUST_POINTS = [
  "Lifetime interactive online study access — yours until you pass",
  "Full access connects automatically to your checkout email",
  "Secure checkout",
  "Independent study aid",
  "Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission",
  "No pass guarantee",
  "7-day satisfaction refund",
];

export const TRUST_BADGES = [
  { icon: "MonitorPlay", label: "Interactive practice access" },
  { icon: "ShieldCheck", label: "Secure checkout" },
  { icon: "BadgeCheck", label: "Independent study aid" },
  { icon: "MapPin", label: "Illinois-focused" },
];

export const DISCLAIMER_TEXT =
  "Illinois Broker Prep is an independent study aid operated by Coursewright. It is not affiliated with, endorsed by, or sponsored by the Illinois Department of Financial and Professional Regulation (IDFPR), PSI, the State of Illinois, or any testing authority. All practice questions are original and are not actual exam questions. This tool is a study aid and does not constitute legal advice or a substitute for official licensing materials.";

export const REFUND_TEXT =
  "We offer a 7-day, no-questions-asked satisfaction refund. If you're not happy with Illinois Broker Prep for any reason, email support@illinoisbrokerprep.com within 7 days of your purchase and we'll refund you in full. After 7 days, sales are final. This is a satisfaction policy only — it is not a pass/fail guarantee, and we never guarantee a passing score or claim to provide official PSI/IDFPR material.";

export const PLATFORM_ACCESS_TEXT =
  "You get lifetime access — yours until you pass — to the full Illinois Broker Prep learning system: source-backed lessons, Question Bank, Exam Mode, Question Coach, Word Bank, dashboard, and every free update as the study library expands.";

export const FAQ = [
  {
    q: "Does this cover both the national and state portions?",
    a: "Yes. You get all three tested areas — national concepts, Illinois state law, and real estate math — with the deepest coverage on Illinois-specific material.",
  },
  {
    q: "Does Exam Mode match the current exam format?",
    a: "Exam Mode includes a 100-question, 150-minute National Broker simulation and a 40-question, 90-minute Illinois Broker simulation. Those counts, time limits, and practice targets follow the current PSI candidate bulletin. They are original practice simulations, not official exams.",
  },
  {
    q: "How current is the question bank?",
    a: "Question records were reviewed June 26, 2026 and organized around the Illinois state outline effective June 24, 2026. The public Coverage page shows the current counts, review date, and official outline source.",
  },
  {
    q: "Are these real exam questions?",
    a: "No. They are original practice questions written to the statutes, rules, source references, and concepts the exam tests. They are not copied from any actual exam.",
  },
  {
    q: "How does Question Coach work?",
    a: "After you answer a practice question, you can ask for a simpler explanation, compare the choices, or clarify the rule being tested. Question Coach is grounded in that question's choices, correct answer, explanation, and source reference when available. AI-enhanced follow-ups use that same focused context when they are available. It is study help, not legal advice.",
  },
  {
    q: "How fast do I get access?",
    a: "Full access connects to the email used at checkout as soon as the paid order reaches us. We also email a direct start link. If automated delivery is delayed, support completes activation within one business day.",
  },
  {
    q: "Do you guarantee I’ll pass?",
    a: "No. No honest prep tool can. What we offer is a 7-day satisfaction refund if it is not the right fit for you.",
  },
  {
    q: "Can I use it on my phone?",
    a: "Yes. It runs in your mobile browser — nothing to download or install.",
  },
  {
    q: "How long do I keep access?",
    a: "Lifetime. Study until you pass, at your own pace, with every update we add along the way. Lifetime access means your access stays active for the operational life of the Illinois Broker Prep online study tool unless the purchase is refunded or the account is shared, resold, or otherwise misused.",
  },
  {
    q: "Is this affiliated with the State of Illinois or the testing provider?",
    a: "No. Illinois Broker Prep is an independent study aid. It is not affiliated with, endorsed by, or sponsored by IDFPR, PSI, the State of Illinois, or any testing authority, and it is not a substitute for official licensing materials or legal advice.",
  },
  {
    q: "Who is this for?",
    a: "Illinois broker candidates who have finished or are finishing the 75-hour course, anyone testing in the next few weeks, and retakers who need focused Illinois-specific review.",
  },
  {
    q: "What do I get after purchase?",
    a: "Your student account unlocks lifetime access — yours until you pass — to the complete interactive online study tool: source-backed lessons, the active question bank across Illinois state law, national concepts, and math, separate National and Illinois exam simulations, Question Coach, Word Bank, missed-question review, bookmarks, explanations, source references, and a weak-area dashboard. The current question counts are always published on the Coverage page.",
  },
  {
    q: "Where do I log in after I buy?",
    a: "Use Sign in in the site navigation or go directly to illinoisbrokerprep.com/study/login. Enter the same email used at checkout and we will send a secure six-digit sign-in code. Existing buyers with an older access code can still use it from that page and keep all saved progress.",
  },
  {
    q: "Is checkout secure?",
    a: "Yes. Checkout is handled by a secure third-party payment provider. Illinois Broker Prep does not see or store your card details.",
  },
  {
    q: "Will I get updates?",
    a: "Yes. Buyers receive free updates as the product expands — at no extra cost, for the life of your access.",
  },
];


// Compliance-safe trust/safety badges shown directly beneath major CTAs.
// NOTE: intentionally no "money-back pass guarantee" / "official" / "approved".
export const CTA_SAFETY_BADGES = [
  { icon: "MonitorPlay", label: "Interactive study access" },
  { icon: "ShieldCheck", label: "Secure checkout" },
  { icon: "RotateCcw", label: "7-day satisfaction refund" },
  { icon: "Ban", label: "No subscription" },
  { icon: "BadgeCheck", label: "Independent study aid" },
];
